import {createDrawingCore} from './drawingCore';
import React, {useMemo, useState} from 'react';
import {createSpreadsheetCore} from './spreadsheetCore';
import {getWorkbookSheets} from './workbookModel';

const sheet=createSpreadsheetCore(),ink=createDrawingCore();
const empty={};
const bound=(value,min,max,fallback)=>Math.min(max,Math.max(min,Number(value)||fallback));

export default function StaticSpreadsheetViewer({data}) {
    const sheets=useMemo(()=>getWorkbookSheets(data),[data]);
    const [selected,setSelected]=useState(null);
    const active=sheets.find(item=>item.id===selected) || sheets.find(item=>item.id===data?.activeSheetId) || sheets[0];
    if(!data)return <div className="p-4 text-gray-500 bg-gray-50 border rounded-md text-sm">No spreadsheet data available.</div>;
    return <div>
        {(sheets.length>1 || sheets[0].name!=='Sheet 1') && <div role="tablist" aria-label="Saved workbook sheets" className="flex gap-2 overflow-x-auto rounded-t-lg border border-purple-200 bg-purple-50 p-2">
            {sheets.map((item,index)=><button type="button" role="tab" key={item.id} aria-selected={item.id===active.id} tabIndex={item.id===active.id?0:-1}
                className={'shrink-0 rounded-md px-3 py-2 text-sm '+(item.id===active.id?'bg-purple-600 text-white':'bg-white text-purple-700')}
                onClick={()=>setSelected(item.id)} onKeyDown={event=>{
                    if(!['ArrowLeft','ArrowRight','Home','End'].includes(event.key))return;
                    event.preventDefault();const next=event.key==='Home'?0:event.key==='End'?sheets.length-1:(index+(event.key==='ArrowLeft'?-1:1)+sheets.length)%sheets.length;
                    setSelected(sheets[next].id);event.currentTarget.parentElement.children[next].focus();
                }}>{item.name}</button>)}
        </div>}
        <SingleSheetViewer key={active.id} data={active.data}/>
    </div>;
}

function SingleSheetViewer({data}) {
    const cells=data?.cellData || empty,merges=data?.mergedCells || empty;
    const drawings=useMemo(()=>ink.normalize(data?.drawings),[data?.drawings]);
    const grid=useMemo(()=>{
        let maxCol=5,maxRow=10;
        if(drawings.strokes.length){
            const size=ink.bounds(drawings);let x=44,y=28;
            for(let col=0;col<26 && x<size.width;col++){x+=bound(data.columnWidths?.[col],40,600,80);maxCol=Math.max(maxCol,col);}
            for(let row=1;row<=200 && y<size.height;row++){y+=bound(data.rowHeights?.[row],24,400,28);maxRow=Math.max(maxRow,row);}
        }
        const hidden=new Set(),spans={};
        Object.keys(cells).forEach(id=>{const point=sheet.coords(id);if(point && point.col<52 && point.row<=200){maxCol=Math.max(maxCol,point.col);maxRow=Math.max(maxRow,point.row);}});
        Object.entries(merges).forEach(([id,merge])=>{
            const point=sheet.coords(id);if(!point || point.col>=52 || point.row>200)return;
            const colspan=Math.floor(bound(merge.colspan,1,52-point.col,1)),rowspan=Math.floor(bound(merge.rowspan,1,201-point.row,1));
            spans[id]={colSpan:colspan,rowSpan:rowspan};maxCol=Math.max(maxCol,point.col+colspan-1);maxRow=Math.max(maxRow,point.row+rowspan-1);
            for(let r=0;r<rowspan;r++)for(let c=0;c<colspan;c++)if(r||c)hidden.add(sheet.column(point.col+c)+(point.row+r));
        });
        return {maxCol,maxRow,hidden,spans,results:sheet.calculate(cells)};
    },[cells,merges,drawings,data?.columnWidths,data?.rowHeights]);
    if(!data?.cellData)return <div className="p-4 text-gray-500 bg-gray-50 border rounded-md text-sm">No spreadsheet data available.</div>;
    if(!Object.keys(cells).length && !Object.keys(merges).length && !drawings.strokes.length)return <div className="p-4 text-gray-500 bg-gray-50 border rounded-md text-sm">The spreadsheet is blank.</div>;
    const columns=Array.from({length:grid.maxCol+1},(_,index)=>index);
    const widths=columns.map(col=>bound(data.columnWidths?.[col],40,600,80));
    const header={border:'1px solid #d1d5db',background:'#f3f4f6',color:'#4b5563',fontSize:14,height:28,position:'sticky',zIndex:2};
    return <div className="border rounded-lg bg-gray-50 overflow-auto max-h-[80vh]" style={{fontFamily:'system-ui, sans-serif'}}>
        <div style={{position:'relative',width:'max-content'}}>
        <table aria-label="Saved spreadsheet answer" style={{borderCollapse:'collapse',tableLayout:'fixed',width:44+widths.reduce((a,b)=>a+b,0),background:'white'}}>
            <colgroup><col style={{width:44}}/>{columns.map(col=><col key={col} style={{width:widths[col]}}/>)}</colgroup>
            <thead><tr><th style={{...header,top:0,left:0,zIndex:3}} aria-label="Row numbers"/>{columns.map(col=><th key={col} scope="col" style={{...header,top:0}}>{sheet.column(col)}</th>)}</tr></thead>
            <tbody>{Array.from({length:grid.maxRow},(_,index)=>{
                const row=index+1;
                return <tr key={row} style={{height:bound(data.rowHeights?.[row],24,400,28)}}>
                    <th scope="row" style={{...header,left:0}}>{row}</th>
                    {columns.map(col=>{
                        const id=sheet.column(col)+row;if(grid.hidden.has(id))return null;
                        const cell=cells[id] || {},display={...cell};
                        // Recompute from the raw values so old saved result caches cannot show a wrong answer.
                        if(Object.prototype.hasOwnProperty.call(grid.results,id))display.result=grid.results[id];else delete display.result;
                        return <td key={id} data-cell={id} {...grid.spans[id]} style={{border:'1px solid #d1d5db',padding:'2px 8px',fontSize:14,color:'#1f2937',verticalAlign:'top',whiteSpace:'nowrap',...cell.style,overflow:'hidden',overflowWrap:'anywhere'}}>{sheet.format(display)}</td>;
                    })}
                </tr>;
            })}</tbody>
        </table>
        {drawings.strokes.length>0 && <div style={{position:'absolute',top:0,left:0,pointerEvents:'none'}} dangerouslySetInnerHTML={{__html:ink.svg(drawings)}}/>}
        </div>
    </div>;
}
